import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { articleSchema, type ArticleFormData } from './article-schema';
import {
  useArticleCategories, useCreateCategory,
  useArticleFamilies, useCreateFamily,
  useArticleBrands, useCreateBrand,
  useCreateArticle, useUpdateArticle,
  useNextArticleCode, useNextArticleBarcode,
} from '@/hooks/use-articles';
import { SelectWithCreate } from '@/components/ui/select-with-create';
import { useWarehouses, useCreateWarehouse, useCreateStockMovement, useAllStock } from '@/hooks/use-stock';
import type { Article } from '@erp/data-access/interfaces';
import { ImageGallery } from '@/components/ui/image-gallery';
import {
  Plus, Package, CategoryIcon, ShoppingCart, DollarSign, Warehouse, Check,
} from '@/components/icons';
import {
  getOptionsWithCurrent,
  getRootCategoryOptions,
  getSubCategoryOptions,
  resolveArticleCategorySelection,
  resolveOptionSelection,
} from './article-category-utils';
import {
  Button, Checkbox, Input, Label, Textarea,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@erp/ui';

const UNITS = ['pcs', 'kg', 'm', 'l', 'm2', 'box'] as const;
const VAT_RATES = [19, 13, 7, 0] as const;

export interface ArticleFormProps {
  article?: Article | null;
  onSaved?: () => void;
  /** Custom footer renderer; receives submit button. If omitted, renders inline footer. */
  renderFooter?: (props: { submitting: boolean; submitButton: ReactNode }) => ReactNode;
}

export function ArticleForm({ article, onSaved, renderFooter }: ArticleFormProps) {
  const { t } = useTranslation();
  const isEdit = !!article;
  const { data: nextCode } = useNextArticleCode(!isEdit);
  const { data: nextBarcode } = useNextArticleBarcode(!isEdit);
  const [autoCode, setAutoCode] = useState<string | null>(null);
  const [autoBarcode, setAutoBarcode] = useState<string | null>(null);
  const { data: categories } = useArticleCategories();
  const createCategoryMutation = useCreateCategory();
  const { data: families } = useArticleFamilies();
  const createFamilyMutation = useCreateFamily();
  const { data: brands } = useArticleBrands();
  const createBrandMutation = useCreateBrand();
  const { data: warehouses } = useWarehouses();
  const createWarehouseMutation = useCreateWarehouse();
  const [newWhCode, setNewWhCode] = useState('');
  const [newWhName, setNewWhName] = useState('');
  const [showNewWh, setShowNewWh] = useState(false);
  const [warehouseStocks, setWarehouseStocks] = useState<Record<string, string>>({});
  const [stockLoadedForArticle, setStockLoadedForArticle] = useState<string | null>(null);
  const { data: allStock } = useAllStock();
  const queryClient = useQueryClient();
  const createMutation = useCreateArticle();
  const stockMovementMutation = useCreateStockMovement();
  const updateMutation = useUpdateArticle();
  const resolvedArticleCategories = useMemo(
    () => article
      ? resolveArticleCategorySelection(article, categories ?? [])
      : { categoryId: '', subFamily: '' },
    [article, categories],
  );
  const resolvedArticleFamily = useMemo(
    () => article ? resolveOptionSelection(article.family, families ?? []) : '',
    [article, families],
  );
  const resolvedArticleBrand = useMemo(
    () => article ? resolveOptionSelection(article.brand, brands ?? []) : '',
    [article, brands],
  );

  useEffect(() => {
    if (isEdit && article && allStock && warehouses && stockLoadedForArticle !== article.id) {
      const stocks: Record<string, string> = {};
      for (const s of allStock) {
        if (s.articleId === article.id) {
          const current = Number(stocks[s.warehouseId] || 0) + s.quantity;
          stocks[s.warehouseId] = String(current);
        }
      }
      setWarehouseStocks(stocks);
      setStockLoadedForArticle(article.id);
    }
    if (!isEdit && stockLoadedForArticle !== null) {
      setWarehouseStocks({});
      setStockLoadedForArticle(null);
    }
  }, [isEdit, article, allStock, warehouses, stockLoadedForArticle]);

  const {
    register, handleSubmit, reset, setValue, watch, control,
    formState: { errors, isSubmitting, dirtyFields },
  } = useForm<ArticleFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Zod v4 + RHF resolver compat
    resolver: zodResolver(articleSchema) as any,
    defaultValues: {
      code: '', name: '', barcode: '', description: '',
      categoryId: '', family: '', subFamily: '', brand: '',
      unit: 'pcs', images: [], warehouseId: '',
      purchasePriceHt: 0, supplierDiscount: 0,
      salePriceHt: 0, salePriceTtc: 0, vatRate: 19,
      stockMin: 0, isActive: true, isInvoiceable: true,
    },
  });

  useEffect(() => {
    if (article) {
      reset({
        code: article.code, name: article.name,
        barcode: article.barcode ?? '', description: article.description ?? '',
        categoryId: resolvedArticleCategories.categoryId, family: resolvedArticleFamily,
        subFamily: resolvedArticleCategories.subFamily, brand: resolvedArticleBrand,
        unit: article.unit, images: article.images ?? [], warehouseId: '',
        purchasePriceHt: article.purchasePriceHt,
        supplierDiscount: article.supplierDiscount,
        salePriceHt: article.salePriceHt, salePriceTtc: article.salePriceTtc,
        vatRate: article.vatRate, stockMin: article.stockMin,
        isActive: article.isActive, isInvoiceable: article.isInvoiceable,
      });
      setAutoCode(null);
      setAutoBarcode(null);
    } else {
      reset({
        code: nextCode ?? '', name: '',
        barcode: nextBarcode ?? '', description: '',
        categoryId: '', family: '', subFamily: '', brand: '',
        unit: 'pcs', images: [], warehouseId: '',
        purchasePriceHt: 0, supplierDiscount: 0,
        salePriceHt: 0, salePriceTtc: 0, vatRate: 19,
        stockMin: 0, isActive: true, isInvoiceable: true,
      });
      setAutoCode(nextCode ?? null);
      setAutoBarcode(nextBarcode ?? null);
    }
  }, [
    article,
    reset,
    nextCode,
    nextBarcode,
    resolvedArticleCategories.categoryId,
    resolvedArticleCategories.subFamily,
    resolvedArticleFamily,
    resolvedArticleBrand,
  ]);

  useEffect(() => {
    const current = watch('warehouseId');
    if (!current && warehouses?.length) {
      const defaultWh = warehouses.find((w) => w.isDefault) ?? warehouses[0];
      if (defaultWh) setValue('warehouseId', defaultWh.id);
    }
  }, [warehouses, watch, setValue]);

  const purchasePriceHt = watch('purchasePriceHt') || 0;
  const supplierDiscount = Number(watch('supplierDiscount')) || 0;
  const salePriceHt = watch('salePriceHt') || 0;
  const salePriceTtc = watch('salePriceTtc') || 0;
  const vatRate = watch('vatRate') || 0;
  const watchedCategoryId = watch('categoryId');
  const selectedCategoryId = watchedCategoryId || (!dirtyFields.categoryId ? resolvedArticleCategories.categoryId : '');

  const [localMargin, setLocalMargin] = useState('');
  const [localBenefit, setLocalBenefit] = useState('');
  const [editingField, setEditingField] = useState<'margin' | 'benefit' | null>(null);

  const r3 = (n: number) => Math.round(n * 1000) / 1000;
  // Prix d'achat net = brut - remise fournisseur ; TTC = net × (1 + TVA)
  const purchasePriceNet = r3(purchasePriceHt * (1 - supplierDiscount / 100));
  const purchasePriceTtc = r3(purchasePriceNet * (1 + vatRate / 100));
  const calcMargin = salePriceHt > 0 && purchasePriceNet > 0
    ? r3(((salePriceHt - purchasePriceNet) / purchasePriceNet) * 100)
    : 0;
  const calcBenefit = r3(salePriceHt - purchasePriceNet);

  const setHt = (ht: number) => {
    setValue('salePriceHt', ht);
    setValue('salePriceTtc', r3(ht * (1 + vatRate / 100)));
  };
  const setTtc = (ttc: number) => {
    setValue('salePriceTtc', ttc);
    setValue('salePriceHt', r3(ttc / (1 + vatRate / 100)));
  };
  const setMargin = (m: number) => {
    if (purchasePriceNet > 0) {
      const ht = r3(purchasePriceNet * (1 + m / 100));
      setValue('salePriceHt', ht);
      setValue('salePriceTtc', r3(ht * (1 + vatRate / 100)));
    }
  };
  const setBenefit = (b: number) => {
    const ht = r3(purchasePriceNet + b);
    setValue('salePriceHt', ht);
    setValue('salePriceTtc', r3(ht * (1 + vatRate / 100)));
  };
  const changeVat = (newVat: number) => {
    setValue('vatRate', newVat);
    const ht = watch('salePriceHt') || 0;
    setValue('salePriceTtc', r3(ht * (1 + newVat / 100)));
  };
  const changePurchase = (p: number) => setValue('purchasePriceHt', p);

  const onSubmit = async (data: ArticleFormData) => {
    const { warehouseId: _wh, ...rest } = data;
    // Treat the field as "unchanged" if the user left it matching EITHER
    // the value we autofilled at form-open (autoCode) OR the current
    // peeked value (nextCode). The latter handles offline updates where
    // the local counter advanced while the form was open. Either way,
    // unchanged → strip from payload so the server assigns a fresh
    // sequential value (and we never collide with an existing row).
    const codeUnchanged =
      !isEdit &&
      ((autoCode !== null && data.code === autoCode) ||
        (nextCode != null && data.code === nextCode));
    const barcodeUnchanged =
      !isEdit &&
      ((autoBarcode !== null && data.barcode === autoBarcode) ||
        (nextBarcode != null && data.barcode === nextBarcode));
    const input = {
      ...rest,
      code: codeUnchanged ? undefined : data.code,
      // Si l'utilisateur a gardé le barcode pré-rempli, on demande au backend
      // de consommer le compteur. Sinon on envoie la valeur saisie (ou vide).
      barcode: barcodeUnchanged ? undefined : data.barcode,
      autoGenerateBarcode: barcodeUnchanged,
      categoryId: (dirtyFields.categoryId ? data.categoryId : data.categoryId || resolvedArticleCategories.categoryId) || undefined,
      family: (dirtyFields.family ? data.family : data.family || resolvedArticleFamily) || undefined,
      subFamily: (dirtyFields.subFamily ? data.subFamily : data.subFamily || resolvedArticleCategories.subFamily) || undefined,
      brand: (dirtyFields.brand ? data.brand : data.brand || resolvedArticleBrand) || undefined,
    };

    if (isEdit && article) {
      await updateMutation.mutateAsync({ id: article.id, input });
      const today = new Date().toISOString().slice(0, 10);
      const currentStockByWh: Record<string, number> = {};
      if (allStock) {
        for (const s of allStock) {
          if (s.articleId === article.id) {
            currentStockByWh[s.warehouseId] = (currentStockByWh[s.warehouseId] ?? 0) + s.quantity;
          }
        }
      }
      const adjustPromises = Object.entries(warehouseStocks)
        .map(([whId, qtyStr]) => {
          const desired = Number(qtyStr) || 0;
          const current = currentStockByWh[whId] ?? 0;
          const diff = desired - current;
          if (diff === 0) return null;
          return stockMovementMutation.mutateAsync({
            articleId: article.id,
            warehouseId: whId,
            movementType: 'adjustment',
            quantity: diff,
            referenceType: 'stock_adjustment',
            movementDate: today,
            notes: `Ajustement stock: ${current} → ${desired}`,
          });
        })
        .filter(Boolean);
      if (adjustPromises.length > 0) await Promise.all(adjustPromises);
    } else {
      const created = await createMutation.mutateAsync(input);
      if (created?.id) {
        const today = new Date().toISOString().slice(0, 10);
        const promises = Object.entries(warehouseStocks)
          .filter(([, qtyStr]) => Number(qtyStr) !== 0)
          .map(([whId, qtyStr]) => {
            const qty = Number(qtyStr) || 0;
            return stockMovementMutation.mutateAsync({
              articleId: created.id,
              warehouseId: whId,
              movementType: qty > 0 ? 'in' : 'out',
              quantity: Math.abs(qty),
              referenceType: 'initial_stock',
              movementDate: today,
              notes: 'Stock initial',
            });
          });
        if (promises.length > 0) await Promise.all(promises);
      }
      setWarehouseStocks({});
    }
    // Fire-and-forget invalidations — awaiting them while offline would
    // hang forever because `networkMode: 'offlineFirst'` pauses the
    // triggered refetches and `invalidateQueries` waits for paused
    // queries to settle. The cache is already invalidated synchronously;
    // the actual refetch will run when the network is back.
    void queryClient.invalidateQueries({ queryKey: ['stock'] });
    void queryClient.invalidateQueries({ queryKey: ['articles'] });
    onSaved?.();
  };

  const submitButton = (
    <Button type="submit" disabled={isSubmitting}>
      {isSubmitting ? t('common.loading') : t('common.save')}
    </Button>
  );

  const unitVal = watch('unit');
  const isInt = unitVal === 'pcs' || unitVal === 'box';
  const stockStep = isInt ? '1' : '0.001';
  const totalInitialStock = Object.values(warehouseStocks).reduce((s, v) => s + (Number(v) || 0), 0);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      {/* === Identification === */}
      <Section title={t('articles.sections.identification')} icon={<Package size={16} />}>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_220px] gap-4 lg:gap-5">
          <div className="space-y-4 min-w-0">
            <div className="grid grid-cols-2 gap-4">
              <Field label={`${t('articles.code')} *`} htmlFor="af-code" error={errors.code && t(errors.code.message!)}>
                <Input id="af-code" {...register('code')} />
              </Field>
              <Field label={t('articles.barcode')} htmlFor="af-barcode">
                <Input id="af-barcode" {...register('barcode')} />
              </Field>
            </div>
            <Field label={`${t('articles.name')} *`} htmlFor="af-name" error={errors.name && t(errors.name.message!)}>
              <Input id="af-name" {...register('name')} />
            </Field>
            <Field label={t('articles.description')} htmlFor="af-description">
              <Textarea id="af-description" {...register('description')} rows={3} />
            </Field>
          </div>
          <div className="lg:max-w-[220px]">
            <Label className="mb-1.5 block">{t('articles.images')}</Label>
            <ImageGallery
              variant="hero"
              value={watch('images') ?? []}
              onChange={(urls) => setValue('images', urls)}
              bucket="erp-images"
              pathPrefix="articles/"
            />
          </div>
        </div>
      </Section>

      {/* === Classification === */}
      <Section title={t('articles.sections.classification')} icon={<CategoryIcon size={16} />}>
        <div className="grid grid-cols-2 gap-4">
          <Field label={t('articles.category')}>
            <Controller
              control={control}
              name="categoryId"
              defaultValue=""
              render={({ field }) => {
                const value = (field.value ?? '') || (!dirtyFields.categoryId ? resolvedArticleCategories.categoryId : '');
                return (
                  <SelectWithCreate
                    value={value}
                    onChange={(v) => {
                      const previousValue = field.value ?? '';
                      field.onChange(v);
                      const findCategory = (candidate: string) => (categories ?? []).find((category) =>
                        category.id === candidate ||
                        category.name.trim().toLocaleLowerCase() === candidate.trim().toLocaleLowerCase(),
                      );
                      const previousCategoryKey = findCategory(previousValue)?.id ?? previousValue;
                      const nextCategoryKey = findCategory(v)?.id ?? v;
                      if (nextCategoryKey !== previousCategoryKey) setValue('subFamily', '');
                    }}
                    options={getRootCategoryOptions(categories ?? [], value)}
                    noneLabel={t('articles.noCategory')}
                    createPlaceholder={t('articles.categories.newCategory')}
                    onCreateNew={async (name) => createCategoryMutation.mutateAsync({ name })}
                    isPending={createCategoryMutation.isPending}
                  />
                );
              }}
            />
          </Field>
          <Field label={t('articles.subCategory')}>
            <Controller
              control={control}
              name="subFamily"
              defaultValue=""
              render={({ field }) => {
                const value = (field.value ?? '') || (!dirtyFields.subFamily ? resolvedArticleCategories.subFamily : '');
                return (
                  <SelectWithCreate
                    value={value}
                    onChange={field.onChange}
                    options={getSubCategoryOptions(categories ?? [], selectedCategoryId, value)}
                    noneLabel="—"
                    createPlaceholder={t('articles.categories.newCategory')}
                    onCreateNew={async (name) => {
                      const parentId = watch('categoryId');
                      return createCategoryMutation.mutateAsync({ name, parentId: parentId || undefined });
                    }}
                    isPending={createCategoryMutation.isPending}
                  />
                );
              }}
            />
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <Field label={t('articles.family')}>
            <Controller
              control={control}
              name="family"
              defaultValue=""
              render={({ field }) => {
                const value = (field.value ?? '') || (!dirtyFields.family ? resolvedArticleFamily : '');
                return (
                  <SelectWithCreate
                    value={value}
                    onChange={field.onChange}
                    options={getOptionsWithCurrent(families ?? [], value)}
                    noneLabel="—"
                    createPlaceholder={t('articles.newFamily')}
                    onCreateNew={async (name) => createFamilyMutation.mutateAsync(name)}
                    isPending={createFamilyMutation.isPending}
                  />
                );
              }}
            />
          </Field>
          <Field label={t('articles.brand')}>
            <Controller
              control={control}
              name="brand"
              defaultValue=""
              render={({ field }) => {
                const value = (field.value ?? '') || (!dirtyFields.brand ? resolvedArticleBrand : '');
                return (
                  <SelectWithCreate
                    value={value}
                    onChange={field.onChange}
                    options={getOptionsWithCurrent(brands ?? [], value)}
                    noneLabel="—"
                    createPlaceholder={t('articles.newBrand')}
                    onCreateNew={async (name) => createBrandMutation.mutateAsync({ name })}
                    isPending={createBrandMutation.isPending}
                  />
                );
              }}
            />
          </Field>
          <Field label={t('articles.unit')}>
            <Select value={watch('unit')} onValueChange={(v) => setValue('unit', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {UNITS.map((u) => (
                  <SelectItem key={u} value={u}>{t(`articles.units.${u}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
      </Section>

      {/* === Tarification — Achat === */}
      <Section title={t('articles.sections.purchasePricing')} icon={<ShoppingCart size={16} />}>
        <div className="grid grid-cols-3 gap-4">
          <Field label={t('articles.purchasePriceHt')}>
            <Input type="number" step="0.001"
              value={purchasePriceHt || ''}
              onChange={(e) => changePurchase(Number(e.target.value) || 0)}
            />
          </Field>
          <Field label={t('articles.supplierDiscount')}>
            <Input type="number" step="0.01" {...register('supplierDiscount')} />
          </Field>
          <Field label={t('articles.vatRate')}>
            <Select value={String(vatRate)} onValueChange={(v) => changeVat(Number(v))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {VAT_RATES.map((r) => (
                  <SelectItem key={r} value={String(r)}>{r}%</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label={t('articles.purchasePriceNet')}>
            <Input type="number" value={purchasePriceNet || ''} disabled className="bg-muted" />
          </Field>
          <Field label={t('articles.purchasePriceTtc')}>
            <Input type="number" value={purchasePriceTtc || ''} disabled className="bg-muted" />
          </Field>
        </div>
      </Section>

      {/* === Tarification — Vente === */}
      <Section title={t('articles.sections.salePricing')} icon={<DollarSign size={16} />}>
        <div className="grid grid-cols-4 gap-4">
          <Field label={t('articles.salePriceHt')}>
            <Input type="number" step="0.001"
              value={salePriceHt || ''}
              onChange={(e) => setHt(Number(e.target.value) || 0)}
            />
          </Field>
          <Field label={t('articles.salePriceTtc')}>
            <Input type="number" step="0.001"
              value={salePriceTtc || ''}
              onChange={(e) => setTtc(Number(e.target.value) || 0)}
            />
          </Field>
          <Field label={`${t('articles.margin')} %`}>
            <Input type="number" step="0.1"
              value={editingField === 'margin' ? localMargin : (calcMargin || '')}
              onFocus={() => { setEditingField('margin'); setLocalMargin(String(calcMargin || '')); }}
              onChange={(e) => setLocalMargin(e.target.value)}
              onBlur={() => { setMargin(Number(localMargin) || 0); setEditingField(null); }}
            />
          </Field>
          <Field label={t('articles.benefitAmount')}>
            <Input type="number" step="0.001"
              value={editingField === 'benefit' ? localBenefit : (calcBenefit || '')}
              onFocus={() => { setEditingField('benefit'); setLocalBenefit(String(calcBenefit || '')); }}
              onChange={(e) => setLocalBenefit(e.target.value)}
              onBlur={() => { setBenefit(Number(localBenefit) || 0); setEditingField(null); }}
            />
          </Field>
        </div>
      </Section>

      {/* === Stock === */}
      <Section
        title={t('articles.sections.stock')}
        icon={<Warehouse size={16} />}
        action={
          <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => setShowNewWh(true)}>
            <Plus size={12} className="mr-1" />{t('stock.newWarehouse')}
          </Button>
        }
      >
        {showNewWh && (
          <div className="flex gap-2 items-end">
            <div className="flex-1 space-y-1">
              <label className="text-xs text-muted-foreground">{t('stock.warehouseCode')}</label>
              <Input value={newWhCode} onChange={(e) => setNewWhCode(e.target.value)} className="h-7 text-xs" />
            </div>
            <div className="flex-1 space-y-1">
              <label className="text-xs text-muted-foreground">{t('stock.warehouseName')}</label>
              <Input value={newWhName} onChange={(e) => setNewWhName(e.target.value)} className="h-7 text-xs" />
            </div>
            <Button type="button" size="sm" className="h-7 text-xs" disabled={!newWhCode || !newWhName || createWarehouseMutation.isPending}
              onClick={async () => {
                await createWarehouseMutation.mutateAsync({ code: newWhCode, name: newWhName, isDefault: !warehouses?.length });
                setNewWhCode(''); setNewWhName(''); setShowNewWh(false);
              }}>{t('common.save')}</Button>
            <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setShowNewWh(false)}>{t('common.cancel')}</Button>
          </div>
        )}

        <Field label={t('articles.stockMin')} htmlFor="af-stockMin">
          <Input id="af-stockMin" type="number" step={stockStep} {...register('stockMin')} className="max-w-[200px]" />
        </Field>

        {warehouses && warehouses.length > 0 ? (
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">{t('articles.initialStock')}</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {warehouses.map((w) => (
                <div key={w.id} className="space-y-1">
                  <label className="text-xs text-muted-foreground">{w.name} {w.isDefault && <span className="text-[10px]">({t('stock.isDefault')})</span>}</label>
                  <Input
                    type="number"
                    step={stockStep}
                    value={warehouseStocks[w.id] ?? ''}
                    onChange={(e) => setWarehouseStocks((prev) => ({ ...prev, [w.id]: e.target.value }))}
                    placeholder="0"
                  />
                </div>
              ))}
            </div>
            <div className="space-y-1 max-w-[200px]">
              <label className="text-xs text-muted-foreground font-semibold">{t('articles.totalStock')}</label>
              <Input type="number" value={totalInitialStock} disabled className="bg-muted font-semibold" />
            </div>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">{t('stock.noWarehouses')}</p>
        )}
      </Section>

      {/* === Statut === */}
      <Section title={t('articles.sections.status')} icon={<Check size={16} />}>
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <Checkbox id="af-isActive" checked={field.value} onCheckedChange={(v) => field.onChange(Boolean(v))} />
              )}
            />
            <Label htmlFor="af-isActive" className="cursor-pointer">{t('articles.isActive')}</Label>
          </div>
          <div className="flex items-center gap-2">
            <Controller
              name="isInvoiceable"
              control={control}
              render={({ field }) => (
                <Checkbox id="af-isInvoiceable" checked={field.value} onCheckedChange={(v) => field.onChange(Boolean(v))} />
              )}
            />
            <Label htmlFor="af-isInvoiceable" className="cursor-pointer">{t('articles.isInvoiceable')}</Label>
          </div>
        </div>
      </Section>

      {renderFooter
        ? renderFooter({ submitting: isSubmitting, submitButton })
        : <div className="flex justify-end pt-2">{submitButton}</div>}
    </form>
  );
}

function Section({ title, icon, action, children }: { title: string; icon?: ReactNode; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-xl border bg-card shadow-sm overflow-hidden">
      <header className="flex items-center justify-between gap-2 border-b bg-muted/30 px-4 py-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          {icon && <span className="text-muted-foreground">{icon}</span>}
          {title}
        </h3>
        {action}
      </header>
      <div className="p-4 space-y-4">{children}</div>
    </section>
  );
}

function Field({ label, htmlFor, error, children }: { label: string; htmlFor?: string; error?: string | false | undefined; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
